import { useShow } from '@refinedev/core';
import { Show } from '@refinedev/antd';
import {
  Card,
  Descriptions,
  Tag,
  Avatar,
  Row,
  Col,
  Statistic,
  Button,
  Space,
  Popconfirm,
  message,
  Table,
  Image,
  Modal,
  Input,
  Steps,
  Form,
  Select,
  DatePicker,
  AutoComplete,
  Tooltip,
  Segmented,
  Switch,
  Timeline,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { UserOutlined, CheckOutlined, CloseOutlined, StopOutlined, WalletOutlined, PlusOutlined, MinusOutlined, HistoryOutlined, BellOutlined, CarOutlined, FileTextOutlined, SafetyOutlined, RocketOutlined, IdcardOutlined, EnvironmentOutlined, UploadOutlined, SafetyCertificateOutlined, CheckCircleOutlined, CloseCircleOutlined, PhoneOutlined } from '@ant-design/icons';
import { CallNotesModal } from '../../components/CallNotesModal';
import { PushNotificationModal } from '../../components/PushNotificationModal';
import { useState, useEffect, useCallback } from 'react';
import { InputNumber } from 'antd';
import { apiClient } from '../../lib/api-client';
import { DriverLocationMap } from '../../components/maps';
import { usePermission } from '../../hooks/usePermission';
import { t } from '../../i18n';

interface Vehicle {
  id: number;
  make: string;
  model: string;
  year: number;
  plate: string;
  color: string;
  seats: number;
  isSelected: boolean;
  allowedTariffs?: string[];
  allowedServices?: string[];
  insuranceExpiry?: string | null;
}

interface CarBrand {
  id: number;
  name: string;
  models: { id: number; name: string }[];
}

const TARIFF_OPTIONS = [
  { value: 'ECONOM', label: 'Ekonom' },
  { value: 'STANDARD', label: 'Standart' },
  { value: 'COMFORT', label: 'Komfort' },
  { value: 'BUSINESS', label: 'Biznes' },
  { value: 'SEVEN_PLUS', label: '7+ o\'rinli' },
];

const TARIFF_TAG_COLORS: Record<string, string> = {
  ECONOM: 'default',
  STANDARD: 'blue',
  COMFORT: 'cyan',
  BUSINESS: 'gold',
  SEVEN_PLUS: 'purple',
};

const TARIFF_LABELS: Record<string, string> = {
  ECONOM: 'Ekonom',
  STANDARD: 'Standart',
  COMFORT: 'Komfort',
  BUSINESS: 'Biznes',
  SEVEN_PLUS: '7+',
};

// Services this vehicle is approved to work. Independent of tariffs.
// Stored on Vehicle.allowedServices on the backend.
const SERVICE_OPTIONS = [
  { value: 'INTERREGION_TAXI', label: 'Viloyatlar Aro' },
  { value: 'SHAHARLAR_ARO', label: 'Viloyat Ichida' },
  { value: 'POSTAGE', label: 'Pochta' },
  { value: 'YOL_YOLAKAY', label: 'Savdolashish' },
];

const SERVICE_TAG_COLORS: Record<string, string> = {
  INTERREGION_TAXI: 'geekblue',
  SHAHARLAR_ARO: 'green',
  POSTAGE: 'orange',
  YOL_YOLAKAY: 'magenta',
};

const SERVICE_LABELS: Record<string, string> = {
  INTERREGION_TAXI: 'Viloyatlar Aro',
  SHAHARLAR_ARO: 'Viloyat Ichida',
  POSTAGE: 'Pochta',
  YOL_YOLAKAY: 'Savdolashish',
};

const ALL_SERVICES = ['INTERREGION_TAXI', 'SHAHARLAR_ARO', 'POSTAGE', 'YOL_YOLAKAY'];

interface Document {
  id: number;
  type: string;
  fileUrl: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  rejectedNote?: string | null;
}

interface Driver {
  id: number;
  fullName: string | null;
  phone: string;
  status: 'ONLINE' | 'OFFLINE' | 'BLOCKED' | 'RESTING';
  photoUrl: string | null;
  rating: number;
  totalTrips: number;
  walletBalance: number;
  isDriverVerified: boolean;
  isLicensed: boolean;
  fcmToken: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  vehicles: Vehicle[];
  documents: Document[];
  currentLat?: number;
  currentLong?: number;
  locationUpdatedAt?: string;
  homeRegion?: { id: number; nameUz: string; nameRu: string } | null;
  activityScore?: number;
  // PINFL + Soliq self-employment status
  pinfl: string | null;
  selfEmploymentStatus: 'REGISTERED' | 'NOT_REGISTERED' | null;
  selfEmploymentReason: string | null;
  selfEmploymentData: string | null;
  selfEmploymentCheckedAt: string | null;
  // JSON-encoded array of activity names returned by Soliq
  // /activity-types/self-employment. Stored as Text; parse on read.
  selfEmploymentActivities: string | null;
  // True iff any registered activity is "passenger transport" (Soliq code 73).
  // Informational — drivers without it are not blocked, just flagged.
  taxiActivityAllowed: boolean | null;
  // ИП / YATT block (parallel to самозанятость, same fields).
  ipStatus: 'REGISTERED' | 'NOT_REGISTERED' | null;
  ipReason: string | null;
  ipActivities: string | null;
  ipTaxiActivityAllowed: boolean | null;
  ipCheckedAt: string | null;
  // Komitent (driver registered our company as komissioner)
  komitentStatus: 'REGISTERED' | 'NOT_REGISTERED' | null;
  komitentCompanyName: string | null;
  komitentContractNumber: string | null;
  komitentContractBegin: string | null;
  komitentContractEnd: string | null;
  komitentDeletedDate: string | null;
  komitentCheckedAt: string | null;
  komitentRegisteredByAdminId: number | null;
  // Per-driver commission overrides (null = use global rate)
  interregionCommissionOverride: string | number | null;
  shaharlarAroCommissionOverride: string | number | null;
  yolYolakayCommissionOverride: string | number | null;
  postageCommissionOverride: string | number | null;
  // Current global rates, attached by GET /admin/drivers/:id so the UI can
  // render placeholder defaults without a separate request.
  commissionDefaults?: {
    interregion: number;
    shaharlarAro: number;
    yolYolakay: number;
    postage: number;
  };
  // Headline stats attached by GET /admin/drivers/:id. `cancellationRate`
  // is a percentage with one decimal — e.g. 4.7 means 4.7%.
  stats?: {
    weekTrips: number;
    monthTrips: number;
    lifetimeAssigned: number;
    cancellationCount: number;
    cancellationRate: number;
  };
}

interface ActivityScoreChange {
  id: number;
  previousScore: number;
  newScore: number;
  delta: number;
  reason: string;
  tripType: string | null;
  tripId: number | null;
  note: string | null;
  createdAt: string;
}

export const DriverShow = () => {
  const { can } = usePermission();
  const navigate = useNavigate();
  const { queryResult } = useShow<Driver>();
  const { data, isLoading, refetch } = queryResult;
  const driver = data?.data;

  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [savingInsuranceId, setSavingInsuranceId] = useState<number | null>(null);

  const saveVehicleInsurance = async (vehicleId: number, date: Dayjs | null) => {
    setSavingInsuranceId(vehicleId);
    try {
      await apiClient.put(`/admin/vehicles/${vehicleId}`, {
        insuranceExpiry: date ? date.format('YYYY-MM-DD') : null,
      });
      message.success(t('insurance.savedExpiry'));
      refetch();
    } catch (err: any) {
      message.error(err?.response?.data?.message || t('insurance.saveFailed'));
    } finally {
      setSavingInsuranceId(null);
    }
  };

  // Per-document review state
  const [docRejectModalOpen, setDocRejectModalOpen] = useState(false);
  const [docToReject, setDocToReject] = useState<Document | null>(null);
  const [docRejectNote, setDocRejectNote] = useState('');
  const [docReviewing, setDocReviewing] = useState<number | null>(null);
  const [docReplacing, setDocReplacing] = useState<number | null>(null);
  const [docUploadingType, setDocUploadingType] = useState<string | null>(null);
  const [licenseSaving, setLicenseSaving] = useState(false);

  // Wallet transactions modal
  const [transactionsModalOpen, setTransactionsModalOpen] = useState(false);

  // Verify-with-tariffs modal
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyForm] = Form.useForm();

  // Per-vehicle edit modal
  const [tariffEditOpen, setTariffEditOpen] = useState(false);
  const [tariffEditVehicle, setTariffEditVehicle] = useState<Vehicle | null>(null);
  const [tariffEditSaving, setTariffEditSaving] = useState(false);
  const [tariffEditForm] = Form.useForm();
  const [carBrands, setCarBrands] = useState<CarBrand[]>([]);
  const [selectedMake, setSelectedMake] = useState<string>('');

  // Wallet management state
  const [adjustModalOpen, setAdjustModalOpen] = useState(false);
  const [adjustType, setAdjustType] = useState<'add' | 'deduct'>('add');
  const [adjustAmount, setAdjustAmount] = useState<number | null>(null);
  const [adjustReason, setAdjustReason] = useState('');
  const [adjusting, setAdjusting] = useState(false);
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [txPage, setTxPage] = useState(1);
  const [txTotal, setTxTotal] = useState(0);
  const txPageSize = 20;

  // Trip history state
  const [tripHistory, setTripHistory] = useState<any[]>([]);
  const [tripHistoryLoading, setTripHistoryLoading] = useState(false);

  // Push-notification modal state
  const [pushModalOpen, setPushModalOpen] = useState(false);
  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [pushSending, setPushSending] = useState(false);

  // Operator-call-notes modal state
  const [callNotesOpen, setCallNotesOpen] = useState(false);

  const sendPushToDriver = async () => {
    if (!driver?.id) return;
    if (!pushTitle.trim() || !pushBody.trim()) {
      message.warning('Введите заголовок и текст уведомления');
      return;
    }
    setPushSending(true);
    try {
      const res = await apiClient.post('/admin/notifications/send', {
        title: pushTitle.trim(),
        body: pushBody.trim(),
        targetType: 'USER',
        targetId: driver.id,
      });
      const delivered = res.data?.delivered ?? 0;
      const failed = res.data?.failed ?? 0;
      if (delivered > 0) {
        message.success(`✅ Уведомление доставлено`);
      } else if (failed > 0) {
        message.error(
          'FCM не смог доставить (возможно, у водителя нет push-токена)',
        );
      } else {
        message.info('Отправлено, но FCM не подтвердил доставку');
      }
      setPushModalOpen(false);
      setPushTitle('');
      setPushBody('');
    } catch (err: any) {
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          'Не удалось отправить уведомление',
      );
    } finally {
      setPushSending(false);
    }
  };

  // PINFL + self-employment state
  const [pinflInput, setPinflInput] = useState<string>('');
  const [pinflSaving, setPinflSaving] = useState(false);
  const [selfEmpChecking, setSelfEmpChecking] = useState(false);
  const [selfEmpLegacyChecking, setSelfEmpLegacyChecking] = useState(false);

  // Komitent state
  const [komitentChecking, setKomitentChecking] = useState(false);
  const [komitentRegistering, setKomitentRegistering] = useState(false);
  const [komitentConfirmOpen, setKomitentConfirmOpen] = useState(false);

  useEffect(() => {
    setPinflInput(driver?.pinfl ?? '');
  }, [driver?.pinfl]);

  // Commission overrides state. `null` = "leave blank, use global default";
  // a number = override for this driver. Treat the response shape (Decimal
  // serializes as string) and the input value (number | null) the same way.
  const toNumOrNull = (v: string | number | null | undefined): number | null =>
    v === null || v === undefined || v === '' ? null : Number(v);
  const [commInter, setCommInter] = useState<number | null>(null);
  const [commSha, setCommSha] = useState<number | null>(null);
  const [commYY, setCommYY] = useState<number | null>(null);
  const [commPostage, setCommPostage] = useState<number | null>(null);
  const [commSaving, setCommSaving] = useState(false);

  useEffect(() => {
    setCommInter(toNumOrNull(driver?.interregionCommissionOverride));
    setCommSha(toNumOrNull(driver?.shaharlarAroCommissionOverride));
    setCommYY(toNumOrNull(driver?.yolYolakayCommissionOverride));
    setCommPostage(toNumOrNull(driver?.postageCommissionOverride));
  }, [
    driver?.interregionCommissionOverride,
    driver?.shaharlarAroCommissionOverride,
    driver?.yolYolakayCommissionOverride,
    driver?.postageCommissionOverride,
  ]);

  const saveCommissions = async () => {
    if (!driver?.id) return;
    setCommSaving(true);
    try {
      await apiClient.put(`/admin/drivers/${driver.id}/commissions`, {
        interregionCommissionOverride: commInter,
        shaharlarAroCommissionOverride: commSha,
        yolYolakayCommissionOverride: commYY,
        postageCommissionOverride: commPostage,
      });
      message.success('Комиссии сохранены');
      refetch();
    } catch (err: any) {
      message.error(
        err?.response?.data?.message || 'Не удалось сохранить комиссии',
      );
    } finally {
      setCommSaving(false);
    }
  };

  const savePinfl = async () => {
    if (!driver?.id) return;
    const trimmed = pinflInput.trim();
    if (trimmed && !/^\d{14}$/.test(trimmed)) {
      message.error('PINFL должен быть ровно 14 цифр');
      return;
    }
    setPinflSaving(true);
    try {
      await apiClient.put(`/admin/drivers/${driver.id}/pinfl`, {
        pinfl: trimmed || null,
      });
      message.success('PINFL сохранён');
      refetch();
    } catch (err: any) {
      message.error(
        err?.response?.data?.message || 'Не удалось сохранить PINFL',
      );
    } finally {
      setPinflSaving(false);
    }
  };

  const checkSelfEmployment = async () => {
    if (!driver?.id) return;
    if (!driver.pinfl) {
      message.warning(
        'Сначала сохраните PINFL для этого водителя',
      );
      return;
    }
    setSelfEmpChecking(true);
    try {
      const res = await apiClient.get(
        `/admin/drivers/${driver.id}/self-employment-check`,
      );
      const seStatus = res.data?.status as 'REGISTERED' | 'NOT_REGISTERED';
      const ipStatus = res.data?.ipStatus as 'REGISTERED' | 'NOT_REGISTERED';
      const seCount = (res.data?.activityNames || []).length as number;
      const ipCount = (res.data?.ipActivityNames || []).length as number;
      if (seStatus === 'REGISTERED' && ipStatus === 'REGISTERED') {
        message.success(
          `✅ Самозанятый (${seCount}) + ИП (${ipCount})`,
        );
      } else if (seStatus === 'REGISTERED') {
        const tail = res.data?.taxiAllowed
          ? `(${seCount} активностей, такси разрешено)`
          : `(${seCount} активностей, такси не покрыто)`;
        message.success(`✅ Самозанятый ${tail}`);
      } else if (ipStatus === 'REGISTERED') {
        const tail = res.data?.ipTaxiAllowed
          ? `(${ipCount} активностей, такси разрешено)`
          : `(${ipCount} активностей, такси не покрыто)`;
        message.success(`✅ ИП ${tail}`);
      } else {
        message.warning('❌ Не зарегистрирован ни как самозанятый, ни как ИП');
      }
      refetch();
    } catch (err: any) {
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          'Soliq tekshiruvi muvaffaqiyatsiz',
      );
    } finally {
      setSelfEmpChecking(false);
    }
  };

  // Legacy-API-only diagnostic call. Doesn't persist — just shows a
  // toast with what the legacy endpoint reported, so the operator can
  // compare with the merged result above when the two disagree.
  const checkSelfEmploymentLegacy = async () => {
    if (!driver?.id) return;
    if (!driver.pinfl) {
      message.warning('Сначала сохраните PINFL для этого водителя');
      return;
    }
    setSelfEmpLegacyChecking(true);
    try {
      const res = await apiClient.get(
        `/admin/drivers/${driver.id}/self-employment-check-legacy`,
      );
      const status = res.data?.status as 'REGISTERED' | 'NOT_REGISTERED';
      if (status === 'REGISTERED') {
        message.success(
          `✅ Старый API: зарегистрирован${
            res.data?.reason ? ` — ${res.data.reason}` : ''
          }`,
        );
      } else {
        message.warning(
          `❌ Старый API: не зарегистрирован${
            res.data?.reason ? ` — ${res.data.reason}` : ''
          }`,
        );
      }
    } catch (err: any) {
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          'Старый Soliq endpoint недоступен',
      );
    } finally {
      setSelfEmpLegacyChecking(false);
    }
  };

  const checkKomitent = async () => {
    if (!driver?.id) return;
    if (!driver.pinfl) {
      message.warning('Сначала сохраните PINFL для этого водителя');
      return;
    }
    setKomitentChecking(true);
    try {
      const res = await apiClient.get(
        `/admin/drivers/${driver.id}/komitent-check`,
      );
      const status = res.data?.status as 'REGISTERED' | 'NOT_REGISTERED';
      if (status === 'REGISTERED') {
        message.success(
          `✅ Зарегистрирован как комитент (${res.data?.companyName || ''})`,
        );
      } else {
        message.warning('❌ Не является нашим комитентом');
      }
      refetch();
    } catch (err: any) {
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          'Проверка комитента не удалась',
      );
    } finally {
      setKomitentChecking(false);
    }
  };

  const registerKomitent = async () => {
    if (!driver?.id) return;
    setKomitentRegistering(true);
    try {
      const res = await apiClient.post(
        `/admin/drivers/${driver.id}/komitent-register`,
      );
      const already = res.data?.alreadyRegistered;
      const num = res.data?.contractNumber;
      if (already) {
        message.info(
          'Водитель уже зарегистрирован как наш комитент — данные обновлены',
        );
      } else {
        message.success(
          `✅ Зарегистрирован как комитент. Договор: ${num || '—'}`,
        );
      }
      setKomitentConfirmOpen(false);
      refetch();
    } catch (err: any) {
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          'Не удалось зарегистрировать комитента',
      );
    } finally {
      setKomitentRegistering(false);
    }
  };

  // Operator activity feed — admin audit-log entries for this driver.
  // Loaded once on mount and after each admin action that would change
  // the driver state (verify, reject, block, etc.); the simpler refetch
  // is to just reload when the page mounts and rely on the user
  // refreshing after their own action.
  type OperatorActivityRow = {
    id: number;
    action: string;
    createdAt: string;
    staff: { id: number; username: string; fullName: string | null } | null;
    newValue: any;
    oldValue: any;
  };
  const [operatorActivity, setOperatorActivity] = useState<OperatorActivityRow[]>([]);
  const [operatorActivityLoading, setOperatorActivityLoading] = useState(false);
  const loadOperatorActivity = useCallback(async () => {
    if (!driver?.id) return;
    setOperatorActivityLoading(true);
    try {
      const res = await apiClient.get<OperatorActivityRow[]>(
        `/admin/drivers/${driver.id}/operator-activity`,
      );
      setOperatorActivity(Array.isArray(res.data) ? res.data : []);
    } catch (e: any) {
      // Non-fatal — keep the card visible with an empty list.
      console.warn('Failed to load operator activity:', e);
    } finally {
      setOperatorActivityLoading(false);
    }
  }, [driver?.id]);
  useEffect(() => {
    if (driver?.id) loadOperatorActivity();
  }, [driver?.id, loadOperatorActivity]);

  // Faollik bali state
  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [activityDelta, setActivityDelta] = useState<number | null>(null);
  const [activityNote, setActivityNote] = useState('');
  const [activitySaving, setActivitySaving] = useState(false);
  const [activityHistory, setActivityHistory] = useState<ActivityScoreChange[]>([]);
  const [activityHistoryLoading, setActivityHistoryLoading] = useState(false);
  const [activityHistoryOpen, setActivityHistoryOpen] = useState(false);

  const loadActivityHistory = useCallback(async () => {
    if (!driver?.id) return;
    setActivityHistoryLoading(true);
    try {
      const res = await apiClient.get<ActivityScoreChange[]>(
        `/admin/users/${driver.id}/activity-score/history?limit=50`,
      );
      setActivityHistory(res.data);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Tarixni yuklashda xatolik');
    } finally {
      setActivityHistoryLoading(false);
    }
  }, [driver?.id]);

  const handleAdjustActivityScore = async () => {
    if (!driver?.id || activityDelta == null || !activityNote.trim()) {
      message.error('Bal va izoh kiritilishi shart');
      return;
    }
    setActivitySaving(true);
    try {
      await apiClient.post(`/admin/users/${driver.id}/activity-score/adjust`, {
        delta: activityDelta,
        note: activityNote.trim(),
      });
      message.success('Faollik bali yangilandi');
      setActivityModalOpen(false);
      setActivityDelta(null);
      setActivityNote('');
      refetch();
      if (activityHistoryOpen) loadActivityHistory();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Xatolik');
    } finally {
      setActivitySaving(false);
    }
  };

  // Journey stages computation
  const REQUIRED_DOC_TYPES = ['DRIVER_SELFIE', 'DRIVER_LICENSE_FRONT', 'DRIVER_LICENSE_BACK', 'VEHICLE_REGISTRATION', 'VEHICLE_REGISTRATION_BACK'];

  const journeyStages = (() => {
    if (!driver) return [];

    const docs = driver.documents || [];
    const hasAllRequiredDocs = REQUIRED_DOC_TYPES.every(type =>
      docs.some(d => d.type === type)
    );
    const allDocsApproved = REQUIRED_DOC_TYPES.every(type =>
      docs.some(d => d.type === type && d.status === 'APPROVED')
    );
    const hasRejectedDocs = docs.some(d =>
      REQUIRED_DOC_TYPES.includes(d.type) && d.status === 'REJECTED'
    );

    return [
      { title: t('drivers.stageRegistration'), completed: true, failed: false, icon: <IdcardOutlined /> },
      { title: t('drivers.stageNameEntered'), completed: !!driver.fullName, failed: false, icon: <UserOutlined /> },
      { title: t('drivers.stageVehicleAdded'), completed: (driver.vehicles?.length || 0) > 0, failed: false, icon: <CarOutlined /> },
      { title: t('drivers.stageDocumentsUploaded'), completed: hasAllRequiredDocs, failed: false, icon: <FileTextOutlined /> },
      { title: t('drivers.stageDocumentsApproved'), completed: allDocsApproved, failed: hasRejectedDocs && !allDocsApproved, icon: <SafetyOutlined /> },
      { title: t('drivers.stageDriverVerified'), completed: driver.isDriverVerified, failed: false, icon: <CheckOutlined /> },
      { title: t('drivers.stagePushEnabled'), completed: !!driver.fcmToken, failed: false, icon: <BellOutlined /> },
      { title: t('drivers.stageFirstTrip'), completed: (driver.totalTrips || 0) > 0, failed: false, icon: <RocketOutlined /> },
    ];
  })();

  const currentStepIndex = journeyStages.findIndex(s => !s.completed);

  const loadTransactions = useCallback(async (page = 1) => {
    if (!driver?.id) return;
    setTransactionsLoading(true);
    try {
      const offset = (page - 1) * txPageSize;
      const res = await apiClient.get(`/admin/users/${driver.id}/transactions?limit=${txPageSize}&offset=${offset}`);
      setTransactions(res.data?.data || []);
      if (res.data?.total != null) setTxTotal(res.data.total);
      else setTxTotal(res.data?.data?.length || 0);
      if (res.data?.walletBalance != null) setWalletBalance(res.data.walletBalance);
    } catch {
      // silent
    } finally {
      setTransactionsLoading(false);
    }
  }, [driver?.id]);

  const loadTripHistory = useCallback(async () => {
    if (!driver?.id) return;
    setTripHistoryLoading(true);
    try {
      const res = await apiClient.get('/admin/trips', {
        params: { driverId: driver.id, limit: 20 },
      });
      const items = res.data?.data || res.data?.items || [];
      setTripHistory(Array.isArray(items) ? items : []);
    } catch {
      // silent
    } finally {
      setTripHistoryLoading(false);
    }
  }, [driver?.id]);

  useEffect(() => {
    if (driver?.id) {
      loadTransactions(txPage);
      loadTripHistory();
    }
  }, [driver?.id, loadTransactions, loadTripHistory, txPage]);

  const handleAdjustWallet = async () => {
    if (!adjustAmount || adjustAmount <= 0 || !adjustReason.trim() || !driver?.id) return;
    setAdjusting(true);
    try {
      const res = await apiClient.post(`/admin/users/${driver.id}/adjust-wallet`, {
        amount: adjustAmount,
        reason: adjustReason.trim(),
        type: adjustType,
      });
      message.success(res.data?.message || t('common.success'));
      setWalletBalance(res.data?.user?.walletBalance ?? null);
      setAdjustModalOpen(false);
      setAdjustAmount(null);
      setAdjustReason('');
      loadTransactions();
    } catch (err: any) {
      message.error(err?.response?.data?.message || t('common.error'));
    } finally {
      setAdjusting(false);
    }
  };

  const handleAdminCancelTrip = async (record: any) => {
    try {
      await apiClient.put(`/admin/trips/${record.tripType}/${record.id}`, {
        status: 'CANCELLED',
        cancelReason: 'Admin tomonidan bekor qilindi',
      });
      message.success('Safar bekor qilindi (reyting ta\'sir qilmaydi)');
      loadTripHistory();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const openVerifyModal = () => {
    const selected = driver?.vehicles?.find((v) => v.isSelected);
    const initial = selected?.allowedTariffs && selected.allowedTariffs.length > 0
      ? selected.allowedTariffs
      : ['STANDARD'];
    verifyForm.setFieldsValue({ permittedTariffs: initial });
    setVerifyModalOpen(true);
  };

  const handleVerify = async () => {
    try {
      const values = await verifyForm.validateFields();
      setVerifying(true);
      await apiClient.post(`/admin/drivers/${driver?.id}/verify`, {
        permittedTariffs: values.permittedTariffs,
      });
      message.success(t('drivers.driverVerified'));
      setVerifyModalOpen(false);
      refetch();
    } catch (err: any) {
      if (err?.errorFields) return; // form validation error
      message.error(err?.response?.data?.message || t('common.error'));
    } finally {
      setVerifying(false);
    }
  };

  const loadCarBrands = useCallback(async () => {
    if (carBrands.length > 0) return;
    try {
      const res = await apiClient.get<CarBrand[]>('/car-brands');
      setCarBrands(Array.isArray(res.data) ? res.data : []);
    } catch {
      // non-fatal — admin can still type free-text
    }
  }, [carBrands.length]);

  const openTariffEdit = (vehicle: Vehicle) => {
    setTariffEditVehicle(vehicle);
    setSelectedMake(vehicle.make ?? '');
    tariffEditForm.setFieldsValue({
      make: vehicle.make ?? '',
      model: vehicle.model ?? '',
      year: vehicle.year ?? new Date().getFullYear(),
      plate: vehicle.plate ?? '',
      allowedTariffs: vehicle.allowedTariffs && vehicle.allowedTariffs.length > 0
        ? vehicle.allowedTariffs
        : ['STANDARD'],
      // Default to all four services enabled when the vehicle has no
      // allowedServices yet (legacy rows from before this column existed)
      // — matches the schema-level default.
      allowedServices: vehicle.allowedServices && vehicle.allowedServices.length > 0
        ? vehicle.allowedServices
        : ALL_SERVICES,
      seats: vehicle.seats ?? 4,
      color: vehicle.color ?? '',
    });
    setTariffEditOpen(true);
    loadCarBrands();
  };

  const handleTariffEditSave = async () => {
    if (!tariffEditVehicle) return;
    try {
      const values = await tariffEditForm.validateFields();
      setTariffEditSaving(true);
      // Save vehicle metadata + tariffs in one call (existing endpoint), then
      // services in a second call. Two endpoints kept separate so a failure
      // in one doesn't roll back the other and the audit log stays clear.
      await apiClient.put(`/admin/vehicles/${tariffEditVehicle.id}`, {
        make: values.make?.trim(),
        model: values.model?.trim(),
        year: values.year,
        plate: values.plate?.trim().toUpperCase(),
        allowedTariffs: values.allowedTariffs,
        seats: values.seats,
        color: values.color?.trim(),
      });
      await apiClient.put(`/admin/vehicles/${tariffEditVehicle.id}/services`, {
        allowedServices: values.allowedServices,
      });
      message.success("Avtomobil yangilandi");
      setTariffEditOpen(false);
      setTariffEditVehicle(null);
      refetch();
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || 'Saqlashda xatolik');
    } finally {
      setTariffEditSaving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      message.error(t('drivers.enterRejectReason'));
      return;
    }

    setRejecting(true);
    try {
      await apiClient.post(`/admin/drivers/${driver?.id}/reject`, {
        reason: rejectReason,
      });
      message.success(t('drivers.driverRejected'));
      setRejectModalOpen(false);
      setRejectReason('');
      refetch();
    } catch {
      message.error(t('common.error'));
    } finally {
      setRejecting(false);
    }
  };

  const handleApproveDoc = async (doc: Document) => {
    setDocReviewing(doc.id);
    try {
      await apiClient.put(`/admin/documents/${doc.id}/review`, {
        status: 'APPROVED',
      });
      message.success(t('documents.approveSuccess'));
      refetch();
    } catch {
      message.error(t('common.error'));
    } finally {
      setDocReviewing(null);
    }
  };

  const handleRejectDoc = async () => {
    if (!docToReject) return;
    setDocReviewing(docToReject.id);
    try {
      await apiClient.put(`/admin/documents/${docToReject.id}/review`, {
        status: 'REJECTED',
        rejectedNote: docRejectNote,
      });
      message.success(t('documents.rejectSuccess'));
      setDocRejectModalOpen(false);
      setDocToReject(null);
      setDocRejectNote('');
      refetch();
    } catch {
      message.error(t('common.error'));
    } finally {
      setDocReviewing(null);
    }
  };

  const handleReplaceDocFile = async (doc: Document, file: File) => {
    setDocReplacing(doc.id);
    try {
      const form = new FormData();
      form.append('file', file);
      await apiClient.put(`/admin/documents/${doc.id}/file`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      message.success(t('documents.fileReplaced') || 'Fayl yangilandi');
      refetch();
    } catch (err: any) {
      message.error(err?.response?.data?.message || t('common.error'));
    } finally {
      setDocReplacing(null);
    }
  };

  const handleUploadDocForUser = async (type: string, file: File) => {
    if (!driver) return;
    setDocUploadingType(type);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('type', type);
      // Vehicle photos / tech-passport attach to the active vehicle. Backend
      // also resolves this if omitted, but we send it explicitly when known
      // so re-uploads stay tied to the right car if the driver has more than
      // one.
      if (type.startsWith('VEHICLE_')) {
        const activeVehicle =
          driver.vehicles.find((v) => v.isSelected) || driver.vehicles[0];
        if (activeVehicle) {
          form.append('vehicleId', String(activeVehicle.id));
        }
      }
      await apiClient.post(`/admin/users/${driver.id}/documents`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      message.success(t('documents.uploadSuccess') || 'Hujjat yuklandi');
      refetch();
    } catch (err: any) {
      message.error(err?.response?.data?.message || t('common.error'));
    } finally {
      setDocUploadingType(null);
    }
  };

  const handleSetLicense = async (next: boolean) => {
    if (!driver) return;
    setLicenseSaving(true);
    try {
      await apiClient.put(`/admin/users/${driver.id}/license`, {
        isLicensed: next,
      });
      message.success(
        next
          ? t('drivers.licensedSet') || 'Litsenziyli sifatida belgilandi'
          : t('drivers.unlicensedSet') || 'Litsenziyasiz sifatida belgilandi',
      );
      refetch();
    } catch (err: any) {
      message.error(err?.response?.data?.message || t('common.error'));
    } finally {
      setLicenseSaving(false);
    }
  };

  const handleBlock = async () => {
    try {
      await apiClient.post(`/admin/users/${driver?.id}/block`, {
        reason: 'Admin tomonidan bloklangan',
      });
      message.success(t('drivers.driverBlocked'));
      refetch();
    } catch {
      message.error(t('common.error'));
    }
  };

  const handleUnblock = async () => {
    try {
      await apiClient.post(`/admin/users/${driver?.id}/unblock`);
      message.success(t('drivers.driverUnblocked'));
      refetch();
    } catch {
      message.error(t('common.error'));
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('uz-UZ').format(value) + ' ' + t('common.sum');
  };

  const documentTypeLabels: Record<string, string> = {
    DRIVER_LICENSE_FRONT: t('document.driverLicense'),
    DRIVER_LICENSE_BACK: t('document.driverLicenseBack'),
    DRIVER_SELFIE: t('document.selfie'),
    VEHICLE_REGISTRATION: t('document.techPassport'),
    VEHICLE_REGISTRATION_BACK: t('document.techPassportBack'),
    VEHICLE_PHOTO_FRONT: t('document.carFront'),
    VEHICLE_PHOTO_BACK: t('document.carBack'),
    VEHICLE_PHOTO_LEFT: t('document.carLeft'),
    VEHICLE_PHOTO_RIGHT: t('document.carRight'),
    VEHICLE_PHOTO_INTERIOR: t('document.carInterior'),
    VEHICLE_PHOTO_TRUNK: t('document.carTrunk'),
    VEHICLE_PHOTO_BACK_SEATS: t('document.carBackSeats'),
    VEHICLE_PHOTO_FRONT_SEAT: t('document.carFrontSeat'),
  };

  // Compact field with a small uppercase label above the value. Used inside
  // each vehicle card so the spec row reads cleanly without forcing a wide
  // table layout.
  const FieldBlock = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div>
      <div
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          color: '#8C8C8C',
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 14, color: '#262626' }}>{children}</div>
    </div>
  );

  const renderVehicleCard = (vehicle: Vehicle) => {
    const tariffs =
      vehicle.allowedTariffs && vehicle.allowedTariffs.length > 0
        ? vehicle.allowedTariffs
        : ['STANDARD'];
    const services =
      vehicle.allowedServices && vehicle.allowedServices.length > 0
        ? vehicle.allowedServices
        : ALL_SERVICES;

    return (
      <Card
        key={vehicle.id}
        size="small"
        styles={{ body: { padding: 16 } }}
        style={{
          marginBottom: 12,
          borderRadius: 10,
          border: vehicle.isSelected ? '1px solid #B7EB8F' : '1px solid #F0F0F0',
          background: vehicle.isSelected ? '#F6FFED' : '#FFFFFF',
        }}
      >
        {/* Header — make/model + active badge + edit action */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
            marginBottom: 16,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <CarOutlined style={{ fontSize: 18, color: '#1890ff' }} />
            <span style={{ fontSize: 16, fontWeight: 600, color: '#262626' }}>
              {vehicle.make} {vehicle.model}
            </span>
            {vehicle.isSelected ? (
              <Tag color="green" style={{ margin: 0 }}>
                {t('common.active')}
              </Tag>
            ) : (
              <Tag style={{ margin: 0 }}>{t('common.notActive')}</Tag>
            )}
          </div>
          {can('drivers', 'verify') && (
            <Button type="link" size="small" onClick={() => openTariffEdit(vehicle)}>
              Tahrir
            </Button>
          )}
        </div>

        {/* Specs row — wraps to new lines on narrow screens, never forces
            horizontal scroll. */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={12} sm={8} md={6}>
            <FieldBlock label={t('drivers.vehicleYear')}>{vehicle.year}</FieldBlock>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <FieldBlock label={t('drivers.vehiclePlate')}>
              <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{vehicle.plate}</span>
            </FieldBlock>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <FieldBlock label={t('drivers.vehicleColor')}>{vehicle.color}</FieldBlock>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <FieldBlock label={t('drivers.insuranceExpiry')}>
              {can('drivers', 'update') ? (
                <DatePicker
                  value={vehicle.insuranceExpiry ? dayjs(vehicle.insuranceExpiry) : null}
                  format="DD.MM.YYYY"
                  placeholder={t('insurance.pickDate')}
                  disabled={savingInsuranceId === vehicle.id}
                  onChange={(d) => saveVehicleInsurance(vehicle.id, d)}
                  style={{ width: '100%' }}
                  size="small"
                />
              ) : (
                <span>
                  {vehicle.insuranceExpiry
                    ? dayjs(vehicle.insuranceExpiry).format('DD.MM.YYYY')
                    : '—'}
                </span>
              )}
            </FieldBlock>
          </Col>
        </Row>

        {/* Tariflar — full-width tag row */}
        <div style={{ marginBottom: 12 }}>
          <div
            style={{
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              color: '#8C8C8C',
              marginBottom: 6,
            }}
          >
            Tariflar
          </div>
          <Space wrap size={[6, 6]}>
            {tariffs.map((code) => (
              <Tag key={code} color={TARIFF_TAG_COLORS[code] || 'default'} style={{ margin: 0 }}>
                {TARIFF_LABELS[code] || code}
              </Tag>
            ))}
          </Space>
        </div>

        {/* Xizmatlar — full-width tag row */}
        <div>
          <div
            style={{
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              color: '#8C8C8C',
              marginBottom: 6,
            }}
          >
            Xizmatlar
          </div>
          <Space wrap size={[6, 6]}>
            {services.map((code) => (
              <Tag key={code} color={SERVICE_TAG_COLORS[code] || 'default'} style={{ margin: 0 }}>
                {SERVICE_LABELS[code] || code}
              </Tag>
            ))}
          </Space>
        </div>
      </Card>
    );
  };

  return (
    <>
      <Show
        isLoading={isLoading}
        title={t('table.driver')}
        headerButtons={
          <Space>
            {/* Operator call-notes log for this driver */}
            <Tooltip title="Заметки звонков (журнал операторов)">
              <Button
                icon={<PhoneOutlined style={{ color: '#10B981' }} />}
                onClick={() => setCallNotesOpen(true)}
                disabled={!driver?.id}
              >
                Звонки
              </Button>
            </Tooltip>
            {/* Send push notification to this driver */}
            <Tooltip
              title={
                driver?.fcmToken
                  ? 'Отправить push-уведомление водителю'
                  : 'У водителя нет FCM-токена — push не дойдёт'
              }
            >
              <Button
                icon={<BellOutlined />}
                onClick={() => setPushModalOpen(true)}
                disabled={!driver?.id}
              >
                Уведомление
              </Button>
            </Tooltip>
            {can('drivers', 'verify') && !driver?.isDriverVerified && (
              <Button type="primary" icon={<CheckOutlined />} onClick={openVerifyModal}>
                {t('drivers.verify')}
              </Button>
            )}
            {/* Overall driver rejection is always available so the admin
                can revoke verification on a misbehaving verified driver
                or reject docs on an unverified one with a single note. */}
            {can('drivers', 'verify') && (
              <Button
                danger
                icon={<CloseOutlined />}
                onClick={() => setRejectModalOpen(true)}
              >
                {t('drivers.rejectDriver')}
              </Button>
            )}
            {can('drivers', 'block') && (
              // Treat the driver as blocked whenever EITHER `status === 'BLOCKED'`
              // OR `blockedAt` is set. The realtime gateway used to overwrite
              // status to ONLINE on every location ping while leaving
              // `blockedAt` set, so trusting `status` alone hid the Unblock
              // button for genuinely-blocked drivers and let admins try to
              // re-block them instead of unblocking.
              (driver?.status === 'BLOCKED' || !!driver?.blockedAt) ? (
                <Popconfirm
                  title={t('drivers.unblockConfirm')}
                  onConfirm={handleUnblock}
                >
                  <Button type="primary" icon={<CheckOutlined />}>
                    {t('common.unblock')}
                  </Button>
                </Popconfirm>
              ) : (
                <Popconfirm
                  title={t('drivers.blockConfirm')}
                  onConfirm={handleBlock}
                >
                  <Button danger icon={<StopOutlined />}>
                    {t('common.block')}
                  </Button>
                </Popconfirm>
              )
            )}
          </Space>
        }
      >
        {/* Phase 3.6 — companion to UserShow's toggle. Lets ops bounce
            back to the passenger profile when reviewing a dual-role
            user. Always shown on this screen since every visitor here
            already knows the user is a driver. */}
        {driver?.id && (
          <div style={{ marginBottom: 16 }}>
            <Segmented<'client' | 'driver'>
              value="driver"
              onChange={(val) => {
                if (val === 'client') {
                  navigate(`/users/${driver.id}`, { replace: true });
                }
              }}
              options={[
                { label: t('users.viewAsPassenger'), value: 'client' },
                { label: t('users.viewAsDriver'), value: 'driver' },
              ]}
            />
          </div>
        )}
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={8}>
            <Card>
              <div className="text-center">
                <Avatar
                  size={100}
                  src={driver?.photoUrl}
                  icon={<UserOutlined />}
                  style={{ marginBottom: 16 }}
                />
                <h3>{driver?.fullName || t('common.noName')}</h3>
                <p style={{ color: '#7F8C8D' }}>{driver?.phone}</p>
                {driver?.homeRegion && (
                  <p style={{ color: '#7F8C8D', fontSize: 13 }}>
                    <EnvironmentOutlined /> {driver.homeRegion.nameUz}
                  </p>
                )}
                <Space>
                  {driver?.isDriverVerified ? (
                    <Tag color="green" icon={<CheckOutlined />}>
                      {t('drivers.verified')}
                    </Tag>
                  ) : (
                    <Tag color="orange" icon={<CloseOutlined />}>
                      {t('drivers.notVerified')}
                    </Tag>
                  )}
                  <Tag
                    color={
                      driver?.blockedAt
                        ? 'red'
                        : driver?.status === 'ONLINE'
                          ? 'green'
                          : driver?.status === 'BLOCKED'
                            ? 'red'
                            : 'default'
                    }
                  >
                    {driver?.blockedAt ? `🔒 ${t('status.blocked')}` : driver?.status}
                  </Tag>
                </Space>
                {can('drivers', 'verify') && (
                  <div
                    style={{
                      marginTop: 14,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 10,
                    }}
                  >
                    <span style={{ color: '#7F8C8D', fontSize: 13 }}>
                      {t('drivers.license') || 'Litsenziya'}:
                    </span>
                    <Switch
                      checked={!!driver?.isLicensed}
                      loading={licenseSaving}
                      onChange={handleSetLicense}
                      checkedChildren={t('drivers.licensed') || 'Litsenziyli'}
                      unCheckedChildren={
                        t('drivers.unlicensed') || 'Litsenziyasiz'
                      }
                    />
                  </div>
                )}
              </div>
            </Card>

            {/* PINFL + tax status (самозанятость + ИП).
                Both checks run in parallel server-side and persist to
                separate column groups. The button at the bottom triggers
                both — there's no separate "check ИП" button. */}
            <Card
              title={
                <Space>
                  <SafetyCertificateOutlined />
                  <span>PINFL и налоговый статус</span>
                </Space>
              }
              style={{ marginTop: 24 }}
            >
              <Form layout="vertical">
                <Form.Item
                  label="PINFL (14 цифр)"
                  validateStatus={
                    pinflInput && !/^\d{14}$/.test(pinflInput.trim())
                      ? 'error'
                      : ''
                  }
                  help={
                    pinflInput && !/^\d{14}$/.test(pinflInput.trim())
                      ? 'PINFL должен быть ровно 14 цифр'
                      : undefined
                  }
                >
                  <Space.Compact style={{ width: '100%' }}>
                    <Input
                      placeholder="14-значный ПИНФЛ"
                      value={pinflInput}
                      onChange={(e) =>
                        setPinflInput(e.target.value.replace(/\D/g, '').slice(0, 14))
                      }
                      maxLength={14}
                      disabled={!can('drivers', 'update')}
                    />
                    <Button
                      type="primary"
                      onClick={savePinfl}
                      loading={pinflSaving}
                      disabled={
                        !can('drivers', 'update') ||
                        pinflInput === (driver?.pinfl ?? '')
                      }
                    >
                      Сохранить
                    </Button>
                  </Space.Compact>
                </Form.Item>

                <div style={{ marginTop: 8 }}>
                  <Space wrap align="center">
                    <span style={{ color: '#7F8C8D' }}>Статус:</span>
                    {driver?.selfEmploymentStatus === 'REGISTERED' && (
                      <Tag color="green" icon={<CheckCircleOutlined />}>
                        Самозанятый
                      </Tag>
                    )}
                    {driver?.selfEmploymentStatus === 'NOT_REGISTERED' && (
                      <Tag color="red" icon={<CloseCircleOutlined />}>
                        Не зарегистрирован
                      </Tag>
                    )}
                    {!driver?.selfEmploymentStatus && (
                      <Tag color="default">Не проверен</Tag>
                    )}
                    {driver?.selfEmploymentReason && (
                      <span style={{ color: '#7F8C8D', fontSize: 12 }}>
                        ({driver.selfEmploymentReason})
                      </span>
                    )}
                  </Space>
                  {driver?.selfEmploymentCheckedAt && (
                    <div style={{ color: '#95A5A6', fontSize: 12, marginTop: 4 }}>
                      Последняя проверка:{' '}
                      {new Date(driver.selfEmploymentCheckedAt).toLocaleString('uz-UZ')}
                    </div>
                  )}
                </div>

                {/* Registered activities returned by Soliq
                    /activity-types/self-employment. Always shown when present —
                    the taxiActivityAllowed warning below is informational, never
                    a block. Activity names come back as a JSON array in
                    selfEmploymentActivities. */}
                {(() => {
                  let activities: string[] = [];
                  try {
                    if (driver?.selfEmploymentActivities) {
                      const parsed = JSON.parse(driver.selfEmploymentActivities);
                      if (Array.isArray(parsed)) {
                        activities = parsed.filter((s) => typeof s === 'string');
                      }
                    }
                  } catch {
                    // Ignore — old rows may store non-JSON; treat as no activities.
                  }
                  if (activities.length === 0) return null;
                  return (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ color: '#7F8C8D', marginBottom: 6 }}>
                        Зарегистрированные виды деятельности:
                      </div>
                      <Space wrap>
                        {activities.map((name, i) => (
                          <Tag key={i} color="blue" style={{ whiteSpace: 'normal', maxWidth: 480 }}>
                            {name}
                          </Tag>
                        ))}
                      </Space>
                    </div>
                  );
                })()}

                {driver?.selfEmploymentStatus === 'REGISTERED' &&
                  driver?.taxiActivityAllowed === false && (
                    <div
                      style={{
                        marginTop: 12,
                        padding: '8px 12px',
                        background: '#FFFBE6',
                        border: '1px solid #FAAD14',
                        borderRadius: 6,
                        color: '#874D00',
                        fontSize: 13,
                      }}
                    >
                      ⚠️ В списке активностей нет «Перевозка пассажиров легковыми
                      автомобилями» (код 73). Водитель зарегистрирован как самозанятый,
                      но формально его деятельность не покрывает таксомоторные
                      перевозки. Принять решение можно вручную — это предупреждение,
                      не блокировка.
                    </div>
                  )}
                {driver?.selfEmploymentStatus === 'REGISTERED' &&
                  driver?.taxiActivityAllowed === true && (
                    <div
                      style={{
                        marginTop: 12,
                        padding: '8px 12px',
                        background: '#F6FFED',
                        border: '1px solid #52C41A',
                        borderRadius: 6,
                        color: '#135200',
                        fontSize: 13,
                      }}
                    >
                      ✅ Деятельность покрывает пассажирские перевозки (код 73).
                    </div>
                  )}

                {/* ИП / YATT — parallel section. Same shape as самозанятость
                    above, populated by the second Soliq call we run alongside. */}
                <div
                  style={{
                    marginTop: 24,
                    paddingTop: 16,
                    borderTop: '1px dashed #E0E0E0',
                  }}
                >
                  <div style={{ marginBottom: 8, fontWeight: 500 }}>
                    ИП / YATT (Yakka Tartibdagi Tadbirkorlik)
                  </div>
                  <Space wrap align="center">
                    <span style={{ color: '#7F8C8D' }}>Статус:</span>
                    {driver?.ipStatus === 'REGISTERED' && (
                      <Tag color="green" icon={<CheckCircleOutlined />}>
                        Зарегистрирован как ИП
                      </Tag>
                    )}
                    {driver?.ipStatus === 'NOT_REGISTERED' && (
                      <Tag color="red" icon={<CloseCircleOutlined />}>
                        Не ИП
                      </Tag>
                    )}
                    {!driver?.ipStatus && (
                      <Tag color="default">Не проверен</Tag>
                    )}
                    {driver?.ipReason && (
                      <span style={{ color: '#7F8C8D', fontSize: 12 }}>
                        ({driver.ipReason})
                      </span>
                    )}
                  </Space>
                  {driver?.ipCheckedAt && (
                    <div style={{ color: '#95A5A6', fontSize: 12, marginTop: 4 }}>
                      Последняя проверка:{' '}
                      {new Date(driver.ipCheckedAt).toLocaleString('uz-UZ')}
                    </div>
                  )}

                  {(() => {
                    let activities: string[] = [];
                    try {
                      if (driver?.ipActivities) {
                        const parsed = JSON.parse(driver.ipActivities);
                        if (Array.isArray(parsed)) {
                          activities = parsed.filter((s) => typeof s === 'string');
                        }
                      }
                    } catch {
                      // Ignore malformed JSON.
                    }
                    if (activities.length === 0) return null;
                    return (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ color: '#7F8C8D', marginBottom: 6 }}>
                          Зарегистрированные виды деятельности (ИП):
                        </div>
                        <Space wrap>
                          {activities.map((name, i) => (
                            <Tag
                              key={i}
                              color="purple"
                              style={{ whiteSpace: 'normal', maxWidth: 480 }}
                            >
                              {name}
                            </Tag>
                          ))}
                        </Space>
                      </div>
                    );
                  })()}

                  {driver?.ipStatus === 'REGISTERED' &&
                    driver?.ipTaxiActivityAllowed === false && (
                      <div
                        style={{
                          marginTop: 12,
                          padding: '8px 12px',
                          background: '#FFFBE6',
                          border: '1px solid #FAAD14',
                          borderRadius: 6,
                          color: '#874D00',
                          fontSize: 13,
                        }}
                      >
                        ⚠️ ИП-деятельность не включает «Перевозка пассажиров…».
                        Это предупреждение, не блокировка.
                      </div>
                    )}
                  {driver?.ipStatus === 'REGISTERED' &&
                    driver?.ipTaxiActivityAllowed === true && (
                      <div
                        style={{
                          marginTop: 12,
                          padding: '8px 12px',
                          background: '#F6FFED',
                          border: '1px solid #52C41A',
                          borderRadius: 6,
                          color: '#135200',
                          fontSize: 13,
                        }}
                      >
                        ✅ ИП-деятельность покрывает пассажирские перевозки.
                      </div>
                    )}
                </div>

                <Space style={{ marginTop: 16 }} wrap>
                  <Button
                    type="primary"
                    icon={<SafetyCertificateOutlined />}
                    onClick={checkSelfEmployment}
                    loading={selfEmpChecking}
                    disabled={!driver?.pinfl}
                  >
                    Проверить через Soliq.uz (самозанятость + ИП)
                  </Button>
                  {/* Legacy-only diagnostic. The button above already
                      hits both endpoints internally and merges; this one
                      is a debugging aid that only consults the older
                      `self-employment/self-employment-check/{pinfl}`
                      endpoint so an operator can see exactly what it
                      reports independently. Result is shown in a toast
                      and is NOT persisted on the driver record. */}
                  <Button
                    icon={<SafetyCertificateOutlined />}
                    onClick={checkSelfEmploymentLegacy}
                    loading={selfEmpLegacyChecking}
                    disabled={!driver?.pinfl}
                  >
                    Только старый API (диагностика)
                  </Button>
                </Space>
              </Form>
            </Card>

            {/* Komitent (driver registered our company as komissioner).
                Separate from самозанятость — a driver can be in either,
                both, or neither tax structure. Read uses GET ...komitent-check;
                write uses POST ...komitent-register and is gated by an
                explicit confirmation modal because it creates a real Soliq
                contract. */}
            <Card
              title={
                <Space>
                  <IdcardOutlined />
                  <span>Комитент (договор комиссии)</span>
                </Space>
              }
              style={{ marginTop: 24 }}
            >
              <p style={{ color: '#7F8C8D', fontSize: 12, marginBottom: 12 }}>
                Водитель регистрирует нашу компанию (TIN 304988999, "WIP DOER" MCHJ)
                как своего комиссионера. Это отдельный налоговый режим от самозанятости —
                водитель может быть в одном, в обоих или ни в одном.
              </p>

              <div style={{ marginBottom: 12 }}>
                <Space wrap align="center">
                  <span style={{ color: '#7F8C8D' }}>Статус:</span>
                  {driver?.komitentStatus === 'REGISTERED' && (
                    <Tag color="green" icon={<CheckCircleOutlined />}>
                      Наш комитент
                    </Tag>
                  )}
                  {driver?.komitentStatus === 'NOT_REGISTERED' && (
                    <Tag color="red" icon={<CloseCircleOutlined />}>
                      Не зарегистрирован
                    </Tag>
                  )}
                  {!driver?.komitentStatus && (
                    <Tag color="default">Не проверен</Tag>
                  )}
                </Space>
              </div>

              {driver?.komitentStatus === 'REGISTERED' && (
                <Descriptions
                  column={1}
                  size="small"
                  bordered
                  style={{ marginBottom: 12 }}
                >
                  {driver?.komitentCompanyName && (
                    <Descriptions.Item label="Комиссионер">
                      {driver.komitentCompanyName}
                    </Descriptions.Item>
                  )}
                  {driver?.komitentContractNumber && (
                    <Descriptions.Item label="Договор №">
                      <code>{driver.komitentContractNumber}</code>
                    </Descriptions.Item>
                  )}
                  {driver?.komitentContractBegin && (
                    <Descriptions.Item label="Действует с">
                      {dayjs(driver.komitentContractBegin).format('DD.MM.YYYY')}
                    </Descriptions.Item>
                  )}
                  {driver?.komitentContractEnd && (
                    <Descriptions.Item label="Действует до">
                      {dayjs(driver.komitentContractEnd).format('DD.MM.YYYY')}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              )}

              {driver?.komitentCheckedAt && (
                <div style={{ color: '#95A5A6', fontSize: 12, marginBottom: 12 }}>
                  Последняя проверка:{' '}
                  {new Date(driver.komitentCheckedAt).toLocaleString('uz-UZ')}
                </div>
              )}

              <Space wrap>
                <Button
                  icon={<SafetyCertificateOutlined />}
                  onClick={checkKomitent}
                  loading={komitentChecking}
                  disabled={!driver?.pinfl}
                >
                  Проверить через Soliq.uz
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setKomitentConfirmOpen(true)}
                  disabled={
                    !driver?.pinfl ||
                    !can('drivers', 'update') ||
                    driver?.komitentStatus === 'REGISTERED'
                  }
                >
                  {driver?.komitentStatus === 'REGISTERED'
                    ? 'Уже зарегистрирован'
                    : 'Зарегистрировать как комитента'}
                </Button>
              </Space>
            </Card>

            <Modal
              title="Регистрация комитента в Soliq.uz"
              open={komitentConfirmOpen}
              onCancel={() => setKomitentConfirmOpen(false)}
              onOk={registerKomitent}
              confirmLoading={komitentRegistering}
              okText="Да, зарегистрировать"
              okButtonProps={{ danger: true }}
              cancelText="Отмена"
            >
              <p>
                Будет создан договор комиссии в Soliq.uz сроком на <b>10 лет</b>.
              </p>
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label="Водитель">
                  {driver?.fullName || '—'} (PINFL: <code>{driver?.pinfl}</code>)
                </Descriptions.Item>
                <Descriptions.Item label="Комиссионер">
                  "WIP DOER" MCHJ (TIN 304988999)
                </Descriptions.Item>
                <Descriptions.Item label="Договор №">
                  <code>SAFAR-{driver?.id}-{dayjs().format('YYYYMMDD')}</code>
                </Descriptions.Item>
                <Descriptions.Item label="Срок">
                  {dayjs().format('DD.MM.YYYY')} — {dayjs().add(10, 'year').format('DD.MM.YYYY')}
                </Descriptions.Item>
              </Descriptions>
              <p style={{ marginTop: 12, color: '#E67E22' }}>
                ⚠ Эта операция создаёт реальную запись в Soliq.uz и не может быть
                отменена через админ-панель — отзыв договора нужно делать вручную.
              </p>
            </Modal>

            {/* Per-driver commission overrides. Each field is independently
                nullable: clearing the input means "use the global default
                shown in the placeholder". Stored on User.* override columns;
                read by CommissionService.resolveRate. */}
            <Card
              title={
                <Space>
                  <WalletOutlined />
                  <span>Комиссия (%) по типам услуг</span>
                </Space>
              }
              style={{ marginTop: 24 }}
            >
              <p style={{ color: '#7F8C8D', fontSize: 12, marginBottom: 16 }}>
                Оставьте пустым, чтобы использовать глобальную ставку. Значение
                от 0 до 100 переопределит её для этого водителя.
              </p>
              <Form layout="vertical">
                {([
                  {
                    key: 'interregion',
                    label: 'Viloyatlar Aro (межрегиональное такси)',
                    value: commInter,
                    setter: setCommInter,
                    def: driver?.commissionDefaults?.interregion,
                  },
                  {
                    key: 'shaharlarAro',
                    label: 'Viloyat ichida (попутчики между городами)',
                    value: commSha,
                    setter: setCommSha,
                    def: driver?.commissionDefaults?.shaharlarAro,
                  },
                  {
                    key: 'yolYolakay',
                    label: 'Savdolashish (попутный заказ)',
                    value: commYY,
                    setter: setCommYY,
                    def: driver?.commissionDefaults?.yolYolakay,
                  },
                  {
                    key: 'postage',
                    label: 'Pochta (доставка посылок)',
                    value: commPostage,
                    setter: setCommPostage,
                    def: driver?.commissionDefaults?.postage,
                  },
                ] as const).map((row) => (
                  <Form.Item
                    key={row.key}
                    label={row.label}
                    style={{ marginBottom: 12 }}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      value={row.value as any}
                      onChange={(v) =>
                        row.setter(
                          v === null || v === undefined ? null : Number(v),
                        )
                      }
                      min={0}
                      max={100}
                      step={0.5}
                      precision={2}
                      placeholder={
                        row.def != null
                          ? `По умолчанию: ${row.def}%`
                          : 'По умолчанию'
                      }
                      suffix="%"
                      disabled={!can('drivers', 'update')}
                    />
                  </Form.Item>
                ))}
                <Button
                  type="primary"
                  onClick={saveCommissions}
                  loading={commSaving}
                  disabled={!can('drivers', 'update')}
                  style={{ marginTop: 8 }}
                  block
                >
                  Сохранить комиссии
                </Button>
              </Form>
            </Card>

            <Card title={t('users.statistics')} style={{ marginTop: 24 }}>
              <Row gutter={[16, 16]}>
                <Col xs={12} md={6}>
                  <Statistic title={t('table.rating')} value={driver?.rating != null ? Number(driver.rating).toFixed(1) : '-'} />
                </Col>
                <Col xs={12} md={6}>
                  <Statistic title={t('table.trips')} value={driver?.totalTrips || 0} />
                </Col>
                <Col xs={12} md={6}>
                  <Statistic
                    title="Haftalik safar"
                    value={driver?.stats?.weekTrips ?? 0}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Statistic
                    title="Oylik safar"
                    value={driver?.stats?.monthTrips ?? 0}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Col>
                <Col xs={12} md={12}>
                  <Statistic
                    title="Bekor qilish foizi"
                    value={driver?.stats?.cancellationRate ?? 0}
                    suffix={`% (${driver?.stats?.cancellationCount ?? 0} / ${driver?.stats?.lifetimeAssigned ?? 0})`}
                    valueStyle={{
                      color:
                        (driver?.stats?.cancellationRate ?? 0) >= 15
                          ? '#EF4444'
                          : (driver?.stats?.cancellationRate ?? 0) >= 5
                            ? '#F59E0B'
                            : '#10B981',
                    }}
                  />
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={24}>
                  <Statistic
                    title="Faollik bali"
                    value={driver?.activityScore != null ? Number(driver.activityScore).toFixed(1) : '-'}
                    suffix="/ 100"
                    valueStyle={{
                      color:
                        (driver?.activityScore ?? 70) >= 71
                          ? '#10B981'
                          : (driver?.activityScore ?? 70) >= 40
                            ? '#F59E0B'
                            : '#EF4444',
                    }}
                  />
                  {can('drivers', 'update') && (
                    <Space style={{ marginTop: 8 }}>
                      <Button
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={() => setActivityModalOpen(true)}
                      >
                        Balni o'zgartirish
                      </Button>
                      <Button
                        size="small"
                        icon={<HistoryOutlined />}
                        onClick={() => {
                          setActivityHistoryOpen(true);
                          loadActivityHistory();
                        }}
                      >
                        Tarix
                      </Button>
                    </Space>
                  )}
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={24}>
                  <Space align="end" size="small" wrap>
                    <Statistic
                      title={t('drivers.walletBalance')}
                      value={formatCurrency(walletBalance ?? driver?.walletBalance ?? 0)}
                      prefix={<WalletOutlined />}
                    />
                    <Button
                      size="small"
                      icon={<HistoryOutlined />}
                      onClick={() => {
                        setTransactionsModalOpen(true);
                        loadTransactions(1);
                        setTxPage(1);
                      }}
                    >
                      {t('common.history') || 'Tarix'}
                    </Button>
                  </Space>
                  {can('drivers', 'update') && (
                    <Space style={{ marginTop: 8 }}>
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        size="small"
                        onClick={() => { setAdjustType('add'); setAdjustModalOpen(true); }}
                      >
                        {t('drivers.addBalance')}
                      </Button>
                      <Button
                        danger
                        icon={<MinusOutlined />}
                        size="small"
                        onClick={() => { setAdjustType('deduct'); setAdjustModalOpen(true); }}
                      >
                        {t('drivers.deductBalance')}
                      </Button>
                    </Space>
                  )}
                </Col>
              </Row>
            </Card>

            <Card title={t('drivers.journeyStages')} style={{ marginTop: 24 }}>
              <Steps
                direction="vertical"
                size="small"
                current={currentStepIndex === -1 ? journeyStages.length : currentStepIndex}
                items={journeyStages.map((stage, index) => ({
                  title: stage.title,
                  icon: stage.icon,
                  status: stage.completed
                    ? 'finish'
                    : stage.failed
                      ? 'error'
                      : index === currentStepIndex
                        ? 'process'
                        : 'wait',
                  description: stage.completed
                    ? t('drivers.stageCompleted')
                    : stage.failed
                      ? t('drivers.stageFailed')
                      : t('drivers.stagePending'),
                }))}
              />
            </Card>
          </Col>

          <Col xs={24} lg={16}>
            <Card title={t('drivers.vehicles')} style={{ marginBottom: 24 }}>
              {(driver?.vehicles || []).length === 0 ? (
                <div style={{ color: '#8C8C8C', textAlign: 'center', padding: '24px 0' }}>
                  —
                </div>
              ) : (
                (driver?.vehicles || []).map(renderVehicleCard)
              )}
            </Card>

            <Card title={t('drivers.documents')} style={{ marginBottom: 24 }}>
              {(() => {
                const allTypes = Object.keys(documentTypeLabels);
                const presentTypes = new Set(
                  (driver?.documents || []).map((d) => d.type),
                );
                const missingTypes = allTypes.filter((t) => !presentTypes.has(t));
                if (missingTypes.length === 0) return null;
                return (
                  <div
                    style={{
                      marginBottom: 16,
                      padding: 12,
                      background: '#FFFBE6',
                      border: '1px solid #FFE58F',
                      borderRadius: 8,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        marginBottom: 8,
                        color: '#874D00',
                      }}
                    >
                      {t('documents.missingDocuments') ||
                        "Yuklanmagan hujjatlar — admin tomonidan yuklash mumkin"}
                    </div>
                    <Space wrap>
                      {missingTypes.map((type) => (
                        <Button
                          key={type}
                          size="small"
                          icon={<UploadOutlined />}
                          loading={docUploadingType === type}
                          disabled={
                            docUploadingType !== null &&
                            docUploadingType !== type
                          }
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/*,application/pdf';
                            input.onchange = (e) => {
                              const f = (e.target as HTMLInputElement)
                                .files?.[0];
                              if (f) handleUploadDocForUser(type, f);
                            };
                            input.click();
                          }}
                        >
                          {documentTypeLabels[type] || type}
                        </Button>
                      ))}
                    </Space>
                  </div>
                );
              })()}
              <Row gutter={[16, 16]}>
                {driver?.documents?.map((doc) => (
                  <Col xs={24} sm={12} md={8} key={doc.id}>
                    <Card
                      size="small"
                      title={documentTypeLabels[doc.type] || doc.type}
                      extra={
                        <Tag
                          color={
                            doc.status === 'APPROVED'
                              ? 'green'
                              : doc.status === 'REJECTED'
                                ? 'red'
                                : 'orange'
                          }
                        >
                          {doc.status === 'APPROVED'
                            ? t('status.approved')
                            : doc.status === 'REJECTED'
                              ? t('status.rejected')
                              : t('status.pending')}
                        </Tag>
                      }
                    >
                      <Image
                        src={doc.fileUrl}
                        alt={doc.type}
                        style={{ width: '100%', height: 150, objectFit: 'cover' }}
                      />
                      {doc.status === 'REJECTED' && doc.rejectedNote && (
                        <div
                          style={{
                            marginTop: 8,
                            padding: '6px 8px',
                            background: '#FFF1F0',
                            border: '1px solid #FFCCC7',
                            borderRadius: 6,
                            fontSize: 12,
                            color: '#A8071A',
                          }}
                        >
                          <strong>{t('common.reason') || 'Sabab'}:</strong> {doc.rejectedNote}
                        </div>
                      )}
                      {can('drivers', 'verify') && (
                        <Space style={{ marginTop: 8 }} size="small" wrap>
                          <Button
                            size="small"
                            type="primary"
                            icon={<CheckOutlined />}
                            disabled={doc.status === 'APPROVED'}
                            loading={docReviewing === doc.id && doc.status !== 'APPROVED'}
                            onClick={() => handleApproveDoc(doc)}
                          >
                            {t('common.approve') || 'Tasdiqlash'}
                          </Button>
                          {/* Per-document reject removed — driver-level
                              rejection lives in the page header
                              ("Haydovchini rad etish" via the existing
                              reject driver flow) so the admin sends one
                              unified rejection note instead of stacking
                              per-doc notes the driver can't act on
                              individually. */}
                          <Button
                            size="small"
                            icon={<UploadOutlined />}
                            loading={docReplacing === doc.id}
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.accept = 'image/*,application/pdf';
                              input.onchange = (e) => {
                                const f = (e.target as HTMLInputElement).files?.[0];
                                if (f) handleReplaceDocFile(doc, f);
                              };
                              input.click();
                            }}
                          >
                            {t('documents.replaceFile') || "Faylni almashtirish"}
                          </Button>
                        </Space>
                      )}
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card>

            {/* Driver Location Map */}
            <DriverLocationMap
              location={
                driver?.currentLat && driver?.currentLong
                  ? {
                      lat: driver.currentLat,
                      lng: driver.currentLong,
                      updatedAt: driver.locationUpdatedAt,
                      status: driver.status,
                    }
                  : null
              }
              driverName={driver?.fullName || t('table.driver')}
              isLoading={isLoading}
              height={350}
            />
          </Col>
        </Row>

        {/* Trip History Section */}
        <Card
          title={
            <Space>
              <HistoryOutlined />
              <span>{t('drivers.tripHistory')}</span>
            </Space>
          }
          style={{ marginTop: 24 }}
          loading={tripHistoryLoading}
        >
          <Table
            dataSource={tripHistory}
            rowKey="id"
            size="small"
            pagination={{ pageSize: 10, size: 'small' }}
            locale={{ emptyText: t('users.tripsNotFound') }}
            columns={[
              {
                title: t('common.id'),
                dataIndex: 'id',
                key: 'id',
                width: 70,
              },
              {
                title: t('common.type'),
                dataIndex: 'tripType',
                key: 'tripType',
                width: 140,
                render: (type: string) => {
                  const labels: Record<string, { text: string; color: string }> = {
                    INTERREGION_TAXI: { text: t('tripType.interregionTaxi'), color: 'blue' },
                    INTERCITY_TAXI: { text: t('tripType.shaharlarAro'), color: 'cyan' },
                    POSTAGE: { text: t('tripType.postage'), color: 'purple' },
                    YOL_YOLAKAY: { text: t('tripType.yolYolakay'), color: 'orange' },
                  };
                  const l = labels[type] || { text: type, color: 'default' };
                  return <Tag color={l.color}>{l.text}</Tag>;
                },
              },
              {
                title: 'Mijoz',
                key: 'client',
                width: 130,
                render: (_: unknown, record: any) => record.client?.phone || '-',
              },
              {
                title: t('table.route'),
                key: 'route',
                render: (_: unknown, record: any) => {
                  const pickup = record.pickupRegion?.nameUz || record.pickupDistrict?.nameUz || '-';
                  const dest = record.destinationRegion?.nameUz || record.destinationDistrict?.nameUz || '-';
                  return `${pickup} → ${dest}`;
                },
              },
              {
                title: t('common.date'),
                key: 'date',
                width: 120,
                render: (_: unknown, record: any) => {
                  const date = record.requestedAt || record.createdAt;
                  return date ? new Date(date).toLocaleDateString('uz-UZ') : '-';
                },
              },
              {
                title: t('dashboard.revenue'),
                key: 'earnings',
                width: 130,
                render: (_: unknown, record: any) => {
                  const price = record.agreedPrice || record.offeredPrice || 0;
                  return price > 0 ? formatCurrency(price) : '-';
                },
              },
              {
                title: t('table.status'),
                dataIndex: 'status',
                key: 'status',
                width: 130,
                render: (status: string) => {
                  const colors: Record<string, string> = {
                    REQUESTED: 'blue',
                    SEARCHING: 'cyan',
                    ACCEPTED: 'geekblue',
                    DRIVER_ASSIGNED: 'purple',
                    IN_PROGRESS: 'orange',
                    COMPLETED: 'green',
                    CANCELLED: 'red',
                    EXPIRED: 'default',
                  };
                  return <Tag color={colors[status] || 'default'}>{status}</Tag>;
                },
              },
              {
                title: '',
                key: 'actions',
                width: 100,
                render: (_: unknown, record: any) => {
                  const canCancel = !['COMPLETED', 'CANCELLED', 'EXPIRED'].includes(record.status);
                  if (!canCancel || !can('trips', 'cancel')) return null;
                  return (
                    <Popconfirm
                      title="Safarni bekor qilish"
                      description="Haydovchi reytingiga ta'sir qilmaydi"
                      onConfirm={() => handleAdminCancelTrip(record)}
                      okText="Bekor qilish"
                      cancelText="Yo'q"
                      okButtonProps={{ danger: true }}
                    >
                      <Button size="small" danger icon={<CloseOutlined />}>
                        Bekor qilish
                      </Button>
                    </Popconfirm>
                  );
                },
              },
            ]}
          />
        </Card>

        {/* Operatorlar faoliyati — audit log of which staff member did
            what to this driver (verify/reject/block/document review/etc).
            Read from `audit_logs` where entity='Driver' and entityId=driverId. */}
        <Card
          title={
            <Space>
              <SafetyCertificateOutlined />
              <span>Operatorlar faoliyati</span>
            </Space>
          }
          style={{ marginTop: 24 }}
          loading={operatorActivityLoading}
          extra={
            <Button
              size="small"
              icon={<HistoryOutlined />}
              onClick={loadOperatorActivity}
              loading={operatorActivityLoading}
            >
              Yangilash
            </Button>
          }
        >
          {operatorActivity.length === 0 ? (
            <div style={{ color: '#8c8c8c', fontStyle: 'italic' }}>
              Hozircha bu haydovchi bo'yicha operator faoliyati yo'q.
            </div>
          ) : (
            <Timeline
              items={operatorActivity.map((row) => {
                // Map raw action codes to display label + color.
                const meta: { label: string; color: string; icon?: any } =
                  row.action === 'driver.verify'
                    ? { label: 'Tasdiqlandi', color: 'green', icon: <CheckCircleOutlined /> }
                    : row.action === 'driver.reject'
                      ? { label: 'Rad etildi', color: 'red', icon: <CloseCircleOutlined /> }
                      : row.action === 'document.approve'
                        ? { label: 'Hujjat tasdiqlandi', color: 'green', icon: <FileTextOutlined /> }
                        : row.action === 'document.reject'
                          ? { label: 'Hujjat rad etildi', color: 'red', icon: <FileTextOutlined /> }
                          : row.action === 'user.block'
                            ? { label: 'Bloklandi', color: 'red', icon: <StopOutlined /> }
                            : row.action === 'user.unblock'
                              ? { label: 'Blokdan chiqarildi', color: 'green', icon: <CheckOutlined /> }
                              : row.action === 'driver.license.set'
                                ? { label: 'Litsenziya o\'zgartirildi', color: 'blue', icon: <SafetyOutlined /> }
                                : row.action === 'driver.operatorChecked'
                                  ? { label: 'Operator tekshirildi', color: 'blue' }
                                  : { label: row.action, color: 'gray' };
                const staffName =
                  row.staff?.fullName?.trim() ||
                  row.staff?.username ||
                  `Staff #${row.staff?.id ?? '?'}`;
                const reason =
                  row.newValue?.reason ||
                  row.newValue?.rejectedNote ||
                  null;
                const docType = row.newValue?.documentType || null;
                const fromTo =
                  row.newValue?.from !== undefined && row.newValue?.to !== undefined
                    ? `${row.newValue.from} → ${row.newValue.to}`
                    : null;
                return {
                  color: meta.color,
                  dot: meta.icon,
                  children: (
                    <div>
                      <div>
                        <strong>{meta.label}</strong>
                        {docType && (
                          <Tag style={{ marginLeft: 8 }}>{String(docType)}</Tag>
                        )}
                      </div>
                      <div style={{ fontSize: 13, color: '#595959' }}>
                        {staffName}
                      </div>
                      <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                        {dayjs(row.createdAt).format('DD.MM.YYYY HH:mm')}
                      </div>
                      {reason && (
                        <div
                          style={{
                            fontSize: 12,
                            color: '#595959',
                            marginTop: 4,
                            background: '#fafafa',
                            padding: '4px 8px',
                            borderRadius: 4,
                          }}
                        >
                          Sabab: {reason}
                        </div>
                      )}
                      {fromTo && (
                        <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>
                          {fromTo}
                        </div>
                      )}
                    </div>
                  ),
                };
              })}
            />
          )}
        </Card>
      </Show>

      {/* Faollik Bali Adjust Modal */}
      <Modal
        title="Faollik balini o'zgartirish"
        open={activityModalOpen}
        onCancel={() => setActivityModalOpen(false)}
        onOk={handleAdjustActivityScore}
        confirmLoading={activitySaving}
        okText="Saqlash"
        cancelText="Bekor qilish"
      >
        <Form layout="vertical">
          <Form.Item label={`Joriy bal: ${driver?.activityScore != null ? Number(driver.activityScore).toFixed(1) : '-'}`}>
            <InputNumber
              style={{ width: '100%' }}
              value={activityDelta}
              onChange={(v) => setActivityDelta(v as number | null)}
              placeholder="Misol: +5 yoki -10"
              min={-100}
              max={100}
            />
          </Form.Item>
          <Form.Item label="Sabab (izoh)">
            <Input.TextArea
              value={activityNote}
              onChange={(e) => setActivityNote(e.target.value)}
              rows={3}
              placeholder="Nima uchun balni o'zgartirayapsiz?"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Faollik Bali History Modal */}
      <Modal
        title="Faollik bali tarixi"
        open={activityHistoryOpen}
        onCancel={() => setActivityHistoryOpen(false)}
        footer={null}
        width={720}
      >
        <Table
          loading={activityHistoryLoading}
          dataSource={activityHistory}
          rowKey="id"
          pagination={{ pageSize: 20 }}
          columns={[
            {
              title: 'Sana',
              dataIndex: 'createdAt',
              render: (v: string) => new Date(v).toLocaleString('uz-UZ'),
            },
            {
              title: 'Sabab',
              dataIndex: 'reason',
              render: (reason: string) => {
                const labels: Record<string, string> = {
                  TRIP_COMPLETED: t('activity.tripCompleted'),
                  FIVE_STAR_BONUS: t('activity.fiveStarBonus'),
                  FOUR_STAR_BONUS: t('activity.fourStarBonus'),
                  LOW_RATING_2_STAR: t('activity.lowRating2Star'),
                  LOW_RATING_1_STAR: t('activity.lowRating1Star'),
                  DRIVER_CANCELLED: t('activity.driverCancelled'),
                  DRIVER_REJECTED: t('activity.driverRejected'),
                  DRIVER_NO_SHOW: t('activity.driverNoShow'),
                  ADMIN_ADJUSTMENT: t('activity.adminAdjustment'),
                  BLOCK_RECOVERY_RESET: t('activity.blockRecoveryReset'),
                  AUTO_BLOCKED: t('activity.autoBlocked'),
                };
                return labels[reason] || reason;
              },
            },
            {
              title: 'Bal',
              dataIndex: 'delta',
              render: (delta: number) => (
                <Tag color={delta >= 0 ? 'green' : 'red'}>
                  {delta >= 0 ? '+' : ''}
                  {delta.toFixed(1)}
                </Tag>
              ),
            },
            {
              title: 'Yangi bal',
              dataIndex: 'newScore',
              render: (v: number) => v.toFixed(1),
            },
            {
              title: 'Izoh',
              dataIndex: 'note',
              ellipsis: true,
            },
          ]}
        />
      </Modal>

      {/* Verify-with-tariffs Modal */}
      <Modal
        title="Haydovchini tasdiqlash va tariflarni belgilash"
        open={verifyModalOpen}
        onCancel={() => setVerifyModalOpen(false)}
        onOk={handleVerify}
        okText="Tasdiqlash"
        cancelText={t('common.cancel')}
        okButtonProps={{ loading: verifying }}
      >
        <p style={{ marginBottom: 12, color: '#666' }}>
          Haydovchi tanlangan avtomobilini quyidagi tariflarda ishlatishi mumkin. Faqat tanlanganlari mobil ilovada ko'rinadi.
        </p>
        <Form form={verifyForm} layout="vertical">
          <Form.Item
            name="permittedTariffs"
            label="Ruxsat berilgan tariflar"
            rules={[{ required: true, message: 'Kamida bitta tarif tanlang' }]}
          >
            <Select
              mode="multiple"
              placeholder="Tariflarni tanlang"
              options={TARIFF_OPTIONS}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Per-vehicle edit modal */}
      <Modal
        title={
          tariffEditVehicle
            ? `${tariffEditVehicle.make} ${tariffEditVehicle.model} (${tariffEditVehicle.plate}) — Avtomobilni tahrirlash`
            : 'Avtomobilni tahrirlash'
        }
        open={tariffEditOpen}
        onCancel={() => { setTariffEditOpen(false); setTariffEditVehicle(null); }}
        onOk={handleTariffEditSave}
        okText="Saqlash"
        cancelText={t('common.cancel')}
        okButtonProps={{ loading: tariffEditSaving }}
        width={560}
      >
        <Form form={tariffEditForm} layout="vertical">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="make"
                label="Brend"
                rules={[{ required: true, message: 'Brendni kiriting' }]}
              >
                <AutoComplete
                  placeholder="Masalan: Chevrolet"
                  options={carBrands.map((b) => ({ value: b.name }))}
                  filterOption={(input, option) =>
                    (option?.value as string).toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={(value) => {
                    setSelectedMake(value || '');
                    // Clear model when brand changes so admin re-picks
                    const currentModel = tariffEditForm.getFieldValue('model');
                    const brand = carBrands.find((b) => b.name === value);
                    if (brand && !brand.models.some((m) => m.name === currentModel)) {
                      tariffEditForm.setFieldValue('model', '');
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="model"
                label="Model"
                rules={[{ required: true, message: 'Modelni kiriting' }]}
              >
                <AutoComplete
                  placeholder="Masalan: Cobalt"
                  options={(
                    carBrands.find((b) => b.name === selectedMake)?.models || []
                  ).map((m) => ({ value: m.name }))}
                  filterOption={(input, option) =>
                    (option?.value as string).toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="year"
                label="Yil"
                rules={[
                  { required: true, message: 'Yilni kiriting' },
                  {
                    type: 'number',
                    min: 1950,
                    max: new Date().getFullYear() + 1,
                    message: `1950 dan ${new Date().getFullYear() + 1} gacha`,
                  },
                ]}
              >
                <InputNumber
                  min={1950}
                  max={new Date().getFullYear() + 1}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="plate"
                label="Davlat raqami"
                rules={[
                  { required: true, message: 'Raqamni kiriting' },
                  { min: 4, message: 'Kamida 4 ta belgi' },
                ]}
                normalize={(v: string) => (v ? v.toUpperCase() : v)}
              >
                <Input placeholder="01A123BC" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="seats"
                label="O'rindiqlar soni"
                rules={[
                  { required: true, message: "O'rindiqlar soni majburiy" },
                  { type: 'number', min: 1, max: 8, message: "1 dan 8 gacha" },
                ]}
              >
                <InputNumber min={1} max={8} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="color"
                label="Rang"
                rules={[{ required: true, message: 'Rangni kiriting' }]}
              >
                <Input placeholder="Masalan: Oq" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="allowedTariffs"
            label="Ruxsat berilgan tariflar"
            rules={[{ required: true, message: 'Kamida bitta tarif tanlang' }]}
          >
            <Select
              mode="multiple"
              placeholder="Tariflarni tanlang"
              options={TARIFF_OPTIONS}
            />
          </Form.Item>
          <Form.Item
            name="allowedServices"
            label="Ruxsat berilgan xizmatlar"
            tooltip="Haydovchi qaysi xizmat turlarida ishlay olishi: Viloyatlar Aro, Viloyat Ichida, Pochta, Savdolashish"
            rules={[{ required: true, message: 'Kamida bitta xizmat tanlang' }]}
          >
            <Select
              mode="multiple"
              placeholder="Xizmat turlarini tanlang"
              options={SERVICE_OPTIONS}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Reject Modal */}
      <Modal
        title={t('drivers.rejectTitle')}
        open={rejectModalOpen}
        onCancel={() => {
          setRejectModalOpen(false);
          setRejectReason('');
        }}
        onOk={handleReject}
        okText={t('common.reject')}
        cancelText={t('common.cancel')}
        okButtonProps={{ danger: true, loading: rejecting }}
      >
        <p style={{ marginBottom: 16 }}>
          {t('drivers.enterRejectReason')}:
        </p>
        <Input.TextArea
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder={t('drivers.rejectReasonPlaceholder')}
          rows={4}
        />
      </Modal>

      {/* Wallet Transactions Modal */}
      <Modal
        title={<><WalletOutlined /> {t('drivers.transactionHistory')}</>}
        open={transactionsModalOpen}
        onCancel={() => setTransactionsModalOpen(false)}
        footer={null}
        width={820}
      >
        <Table
          loading={transactionsLoading}
          dataSource={transactions}
          rowKey="id"
          size="small"
          pagination={{
            current: txPage,
            pageSize: txPageSize,
            total: txTotal,
            size: 'small',
            showTotal: (total) => `${t('common.total')}: ${total}`,
            onChange: (page) => {
              setTxPage(page);
              loadTransactions(page);
            },
          }}
          columns={[
            {
              title: t('common.type'),
              dataIndex: 'transactionType',
              key: 'type',
              render: (type: string) => {
                const labels: Record<string, { text: string; color: string }> = {
                  TRIP_PAYMENT: { text: t('transactionType.tripPayment'), color: 'green' },
                  COMMISSION: { text: t('transactionType.commission'), color: 'red' },
                  WALLET_TOPUP: { text: t('transactionType.walletTopup'), color: 'blue' },
                  WALLET_WITHDRAWAL: { text: t('transactionType.walletWithdrawal'), color: 'orange' },
                  BONUS_CREDIT: { text: t('transactionType.bonusCredit'), color: 'cyan' },
                  BONUS_DEBIT: { text: t('transactionType.bonusDebit'), color: 'purple' },
                  REFERRAL_BONUS: { text: t('transactionType.referralBonus'), color: 'geekblue' },
                  INCENTIVE_BONUS: { text: t('transactionType.incentiveBonus'), color: 'lime' },
                  REFUND: { text: t('transactionType.refund'), color: 'gold' },
                };
                const l = labels[type] || { text: type, color: 'default' };
                return <Tag color={l.color}>{l.text}</Tag>;
              },
            },
            {
              title: t('common.amount'),
              dataIndex: 'amount',
              key: 'amount',
              render: (amount: number, record: any) => {
                const isOut = ['COMMISSION', 'BONUS_DEBIT', 'WALLET_WITHDRAWAL'].includes(record.transactionType);
                return (
                  <span style={{ color: isOut ? '#ff4d4f' : '#52c41a', fontWeight: 600 }}>
                    {isOut ? '-' : '+'}{formatCurrency(amount)}
                  </span>
                );
              },
            },
            {
              title: t('common.description'),
              dataIndex: 'description',
              key: 'description',
              ellipsis: true,
            },
            {
              title: t('common.date'),
              dataIndex: 'timestamp',
              key: 'timestamp',
              render: (ts: string) => new Date(ts).toLocaleString('uz-UZ'),
            },
          ]}
        />
      </Modal>

      {/* Per-document Reject Modal */}
      <Modal
        title={
          docToReject
            ? `${documentTypeLabels[docToReject.type] || docToReject.type} — ${t('common.reject') || 'Rad etish'}`
            : (t('common.reject') || 'Rad etish')
        }
        open={docRejectModalOpen}
        onCancel={() => {
          setDocRejectModalOpen(false);
          setDocToReject(null);
          setDocRejectNote('');
        }}
        onOk={handleRejectDoc}
        okText={t('common.reject') || 'Rad etish'}
        cancelText={t('common.cancel')}
        okButtonProps={{
          danger: true,
          loading: docReviewing != null && docToReject?.id === docReviewing,
        }}
      >
        <p style={{ marginBottom: 16 }}>
          {t('drivers.enterRejectReason')}:
        </p>
        <Input.TextArea
          value={docRejectNote}
          onChange={(e) => setDocRejectNote(e.target.value)}
          placeholder={t('drivers.rejectReasonPlaceholder')}
          rows={4}
        />
      </Modal>

      {/* Wallet Adjust Modal */}
      <Modal
        title={adjustType === 'add' ? t('drivers.addBalance') : t('drivers.deductBalance')}
        open={adjustModalOpen}
        onCancel={() => { setAdjustModalOpen(false); setAdjustAmount(null); setAdjustReason(''); }}
        onOk={handleAdjustWallet}
        okText={adjustType === 'add' ? t('users.addFunds') : t('users.deductFunds')}
        okButtonProps={{
          danger: adjustType === 'deduct',
          loading: adjusting,
          disabled: !adjustAmount || adjustAmount <= 0 || !adjustReason.trim(),
        }}
        cancelText={t('common.cancel')}
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('users.amountLabel')} ({t('common.sum')})</label>
          <InputNumber
            style={{ width: '100%' }}
            min={1}
            max={100000000}
            value={adjustAmount}
            onChange={(v) => setAdjustAmount(v)}
            placeholder={t('users.amountRequired')}
            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
            parser={(value) => Number(value!.replace(/\s/g, ''))}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('users.reasonLabel')}</label>
          <Input.TextArea
            value={adjustReason}
            onChange={(e) => setAdjustReason(e.target.value)}
            placeholder={t('users.reasonRequired')}
            rows={3}
          />
        </div>
      </Modal>

      <PushNotificationModal
        open={pushModalOpen}
        userId={driver?.id ?? null}
        userName={driver?.fullName ?? undefined}
        userPhone={driver?.phone}
        hasFcmToken={!!driver?.fcmToken}
        onClose={() => setPushModalOpen(false)}
      />

      {/* Old inline push-modal kept to avoid an empty fragment — now hidden */}
      {false && (
      <Modal
        open={false}
        onCancel={() => {}}
        onOk={() => {}}
        okText="Отправить"
        cancelText="Отмена"
      >
        {!driver?.fcmToken && (
          <div
            style={{
              background: '#FFF7E6',
              border: '1px solid #FFD591',
              padding: 8,
              borderRadius: 6,
              marginBottom: 12,
              fontSize: 13,
              color: '#874D00',
            }}
          >
            ⚠️ У водителя нет FCM-токена — push, скорее всего, не дойдёт.
            Уведомление всё равно сохранится в БД и будет видно в приложении
            при следующем запуске.
          </div>
        )}
        <div style={{ marginBottom: 12 }}>
          <label
            style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}
          >
            Заголовок
          </label>
          <Input
            value={pushTitle}
            onChange={(e) => setPushTitle(e.target.value)}
            placeholder="Yangi xabar"
            maxLength={100}
            showCount
          />
        </div>
        <div>
          <label
            style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}
          >
            Текст
          </label>
          <Input.TextArea
            value={pushBody}
            onChange={(e) => setPushBody(e.target.value)}
            placeholder="Введите текст уведомления..."
            rows={4}
            maxLength={300}
            showCount
          />
        </div>
      </Modal>
      )}

      {/* Operator call-notes modal */}
      <CallNotesModal
        open={callNotesOpen}
        userId={driver?.id ?? null}
        userName={driver?.fullName ?? undefined}
        userPhone={driver?.phone}
        currentColor={(driver as any)?.callIconColor ?? null}
        onClose={() => setCallNotesOpen(false)}
        onChanged={() => refetch()}
      />
    </>
  );
};
